/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from 'react';

import type { PaymentVendors } from '@swf/cms';
import Cross from '@swf/core/assets/icons/cross.svg';
import Tick from '@swf/core/assets/icons/tick.svg';

import styles from './CMSPaymentVendors.scss';

interface Header {
  method: string;
  deposit: string;
  cashout: string;
  fdeposit: string;
}

interface Vendor {
  name: string;
  url: string;
  logo: string;
  deposit: string;
  cashout: string;
  fdeposit: string;
}

interface Vendors {
  mobileVendor: Vendor[];
  vendor: Vendor[];
}

const VendorType = 'vendor';

const CMSPaymentVendors: React.FC<PaymentVendors> = ({ countries, start_at, base_url }) => {
  const [headers, setHeaders] = useState<Header>();
  const [vendors, setVendors] = useState<Vendors>();
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find((country) => country.code === start_at)?.code || countries[0].code
  );

  useEffect(() => {
    const load = async () => {
      try {
        const url = `${base_url}/${selectedCountry.toLowerCase()}.xml`;
        const text = await (await fetch(url, { mode: 'cors', signal })).text();
        const items = [
          ...new window.DOMParser().parseFromString(text, 'text/xml').childNodes.item(0).childNodes
        ] as HTMLElement[];

        const data = items.reduce(
          (dataItems, item) => {
            if (item.attributes) {
              const attributeValues = [...item.attributes].reduce((attributes, nodeItem) => {
                const { nodeName, nodeValue } = nodeItem;
                attributes[nodeName] = nodeValue;
                return attributes;
              }, []);
              dataItems[item.nodeName].push(attributeValues);
            }

            return dataItems;
          },
          { headings: [], vendor: [], mobileVendor: [] }
        );

        setHeaders(data.headings[0]);
        setVendors({ vendor: data.vendor, mobileVendor: data.mobileVendor });
      } catch {
        setHeaders(undefined);
        setVendors(undefined);
      }
    };

    const abortController = new AbortController();
    const { signal } = abortController;

    load();

    return () => abortController.abort();
  }, [base_url, countries, selectedCountry]);

  return countries ? (
    <div className={styles.paymentVendorsSection}>
      <div className={styles.controlContainer}>
        <select
          className={styles.country}
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
        >
          {countries.map((country) => (
            <option value={country.code} key={country.code}>
              {country.title}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.vendorTable}>
          <thead>
            {headers && (
              <tr>
                <th>&nbsp;</th>
                <th>{headers.method}</th>
                <th>{headers.deposit}</th>
                <th>{headers.cashout}</th>
              </tr>
            )}
          </thead>
          <tbody>
            {vendors &&
              vendors[VendorType].map((item: Vendor, index: number) => (
                <tr key={index}>
                  <td>
                    <a href={item.url}>
                      <img src={item.logo} alt={item.name}></img>
                    </a>
                  </td>
                  <td>
                    <a href={item.url}>{item.name}</a>
                  </td>
                  <td>
                    {item.deposit === '1' ? (
                      <Tick className={styles.tickIcon} />
                    ) : (
                      <Cross className={styles.crossIcon} />
                    )}
                  </td>
                  <td>
                    {item.cashout === '1' ? (
                      <Tick className={styles.tickIcon} />
                    ) : (
                      <Cross className={styles.crossIcon} />
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : null;
};

export default CMSPaymentVendors;
